// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.SettingsByRange.jsx" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//
// </copyright>
// ----------------------------------------------------------------------

/**
 * SettingsByRange component
 */
import Constants from 'logic/data/Izenda.SettingsByRangeConstants';
import MapConstants from 'logic/data/Izenda.ReportPartMapConstants';
import ReportPartFieldConstants from 'logic/data/Izenda.ReportPartFieldConstants';
import SettingsByRangeModel from 'logic/models/Izenda.SettingsByRangeModel';
import SettingsByRangeItemFieldModel from 'logic/models/Izenda.SettingsByRangeItemFieldModel';
import { toJS } from 'mobx';
import { observer } from 'mobx-react';
import React, { Component } from 'react';
import { isNil, find, forEach, map, getOr, isFunction, assign, concat, flatMap, get, uniqueId } from 'lodash/fp';
import DropDown from 'share/components/controls/Izenda.DropDown';
import Button from 'share/components/controls/Izenda.Button';
import GridLayout from 'share/components/controls/Izenda.GridLayout';
import localizer from 'share/localization/Izenda.Localizer';
import SettingsByRangeItem from './Izenda.SettingsByRangeItemDraggable';
import ReportPartUtils from 'share/utils/Izenda.ReportPartUtils';
import UIConstants from 'logic/data/Izenda.UIConstants';

const DATA_TYPE = Constants.DATA_TYPE;
const VALUE_TYPES = Constants.VALUE_TYPES;

function getDateTimeFormatData(dateTimeFormatDataFn) {
  return isFunction(dateTimeFormatDataFn) ? dateTimeFormatDataFn() : dateTimeFormatDataFn;
}

@observer
export default class SettingsByRange extends Component {
  static propsType = {
    allowNullValueOverwrite: React.PropTypes.bool,
    dataType: React.PropTypes.string,
    settingValue: React.PropTypes.object,
    valueProps: React.PropTypes.object,
    haveName: React.PropTypes.bool
  };

  static getDefaultProps = {
    allowNullValueOverwrite: false,
    haveName: false
  };

  constructor(props) {
    super(props);
    this.model = new SettingsByRangeModel();
    this.model.setSettingValue(props.settingValue);
    const dateTimeFormatData = getDateTimeFormatData(props.dateTimeFormatData);
    this.model.setValueTypeByDataType(props.defaultValueType, dateTimeFormatData);
    this.initData();
  }

  componentWillReceiveProps(nextProps: any) {
    if (this.props.selectedPoint !== nextProps.selectedPoint) {
      this.model.disableEditMode();
    }
    this.model.setSettingValue(nextProps.settingValue);
    const dateTimeFormatData = getDateTimeFormatData(nextProps.dateTimeFormatData);
    this.model.setValueTypeByDataType(nextProps.defaultValueType, dateTimeFormatData);
  }

  cleanUp() {
    const {configReportPart, reportPartContent} = this.props;
    const {model} = this;

    if (!configReportPart || model.selectedValueType === 'value')
      return;

    // if current range settings aren't 'value' settings, we need to clean up the custom container fields.
    const valueSettings = model.settingsValue['value'];
    const customContainers = getOr(null, 'reportPartContent.customContainers', this.props);
    if (valueSettings && customContainers) {
      const thresholdElements = find({name: MapConstants.ThresholdContainer}, customContainers);
      forEach(valueSetting => {
        // clean up dynamic
        if (valueSetting.thresholdType === Constants.THRESHOLD.THRESHOLD_TYPES.DYNAMIC.value && valueSetting.fields.length) {
          forEach(field => {
            const elementToRemove = find(
              x => x.fieldNameAlias === field.fieldName,
              thresholdElements.elements);
            elementToRemove && configReportPart.removeFieldFromContainer(thresholdElements, elementToRemove);
          }, valueSetting.fields);
        }

        // clean up dynamic subtotal
        const thresholdSubtotalElements = find({name: MapConstants.ThresholdSubtotalsContainer}, reportPartContent['customContainers']);
        if (valueSetting.thresholdType === Constants.THRESHOLD.THRESHOLD_TYPES.DYNAMIC_SUBTOTAL.value && valueSetting.fields.length) {
          forEach(field => {
            const elementToRemove = find(
              x => x.fieldNameAlias === field.fieldName,
              thresholdSubtotalElements.elements);
            elementToRemove && configReportPart.removeFieldFromContainer(thresholdSubtotalElements, elementToRemove);
          }, valueSetting.fields);
        }
      }, valueSettings);
    }
  }

  getValue = () => {
    this.cleanUp();
    return toJS(this.model.getSettingValue());
  };

  beforeClosing = (valManager: any, btnType: string, updatePreview = true) => {
    if (btnType === 'cancel') {
      return true;
    }

    const {model, props} = this;
    const {allowNullValueOverwrite, fieldStore, configReportPart, haveName} = props;
    this.valManager = valManager;

    model.removeEmptySettingValue(haveName, allowNullValueOverwrite);

    // Validation:
    if (!model.isExistsUncompleteRangeValue(haveName)) {
      const validateMessage = getOr({}, 'validateMessages', this.props);
      const listNameDup = (haveName && model.checkDuplicateName()) || [];
      const dynamicFieldsMissingList = model.checkEmptyDynamicValues();
      const dynamicSubtotalMissingList = model.checkEmptyDynamicSubtotalValues();

      const dateTimeFormatData = getDateTimeFormatData(this.props.dateTimeFormatData);
      const settingInvaid = model.checkConstrains(
        this.props.dataType,
        getOr(false, 'settingByGroupBy.RANGE_CYCLE', dateTimeFormatData)
      );
      const listRangeDup = settingInvaid.duplicate;
      const invalidRange = settingInvaid.invalidRange;

      let error = [];
      if (dynamicFieldsMissingList && dynamicFieldsMissingList.length) {
        error = concat(error, this.buildValidationError(dynamicFieldsMissingList, validateMessage.FIELD_VALUE));
      }
      if (dynamicSubtotalMissingList && dynamicSubtotalMissingList.length) {
        error = concat(error, this.buildValidationError(dynamicSubtotalMissingList, validateMessage.FIELD_FUNCTION_IS_MISSING));
      }
      if (listNameDup.length > 0) {
        error = concat(error, this.buildValidationError(listNameDup, validateMessage.DUPLICATE_NAME));
      }
      if (listRangeDup.length > 0) {
        error = concat(error, this.buildOverlapRangeError(listRangeDup));
      }
      if (invalidRange.length > 0) {
        error = concat(error, this.buildErrorList(invalidRange, validateMessage.INVALID_RANGE));
      }

      if (error.length > 0) {
        valManager.reset();
        valManager.updateErrors(error);
        return Promise.resolve(false);
      }
    }

    return new Promise(resolve => {

      // update custom container fields
      this.updateCustomContainers().then(() => {
        if (configReportPart && updatePreview) {
          configReportPart.useForThreshold = true;
          configReportPart.refreshDataProcess = true;
          fieldStore.needUpdatePreview(configReportPart);
        }
        resolve(true);
      });
    });
  };

  buildValidationError = (items, message) => {
    const error = map(x => ({
      key: `${x.id}`,
      messages: [message]
    }), items);
    return error;
  };

  buildErrorList = (listNameDup: any, msg: any) => {
    const selected = this.model.selectedValueType;

    return flatMap((item) => {
      if (selected === 'value') {
        return [
          {
            key: item.id,
            messages: [msg]
          }
        ];
      }
      return [
        {
          key: `${item.id}_from`,
          messages: [msg]
        },
        {
          key: `${item.id}_to`,
          messages: [msg]
        }
      ];
    }, listNameDup);
  };

  buildOverlapRangeError = list => {
    const validateMessage = getOr({}, 'validateMessages', this.props);
    const selected = this.model.selectedValueType;
    const msg = getValidationMessage();

    function getValidationMessage() {
      let msg = '';

      switch (selected) {
        case 'value':
          msg = validateMessage.VALUE;
          break;
        case 'rangeValue':
          msg = validateMessage.RANGE_VALUE;
          break;
        case 'rangePercent':
          msg = validateMessage.RANGE_PERCENT;
          break;
        case 'allValues':
          msg = validateMessage.VALUE;
          break;
      }
      return msg;
    }

    return this.buildErrorList(list, msg);
  };

  addNewSetting = () => {
    this.valManager && this.valManager.reset();
    this.model.addNewSetting();
  };

  render() {
    const {haveName} = this.props;
    const model = this.model;
    const selectedValueType = model.selectedValueType;
    const selectedSettingsValue = model.selectedSettingsList || [];

    return (
      <section className="setting-by-range u_paddingAll">
        {/* render header */}
        {this.renderHeader()}

        {/* render grid setting */}
        {selectedSettingsValue.length > 0 && this.renderGrid()}

        {

          selectedValueType === 'none' || (selectedValueType === 'value' && haveName) || (selectedValueType === 'allValues' && selectedSettingsValue.length === 1)
            ? null
            : (<Button
              className="u_floatRight"
              label={localizer.getText('SETTING_BY_RANGE_ADD_SETTING')}
              onClick={this.addNewSetting}
            />)
        }
      </section>
    );
  }

  renderHeader() {
    const {props, model} = this;
    const valueProps = getOr({}, 'valueProps', props);

    return (
      <GridLayout className="izenda-SettingByRange-Header">
        <GridLayout.Row>
          <GridLayout.Col cols={5} className="u_controlHeight">
            <span>{valueProps.title}</span>
          </GridLayout.Col>
          <GridLayout.Col cols={7}>
            <DropDown
              options={getOptions()}
              value={model.selectedValueType}
              labelField="label"
              valueField="key"
              onChange={changeValueType.bind(this)}
            />
          </GridLayout.Col>
        </GridLayout.Row>
      </GridLayout>
    );

    function getOptions() {
      const getValueTypes = (input) => {
        const arr = [];
        for (let key in VALUE_TYPES) {
          if (!props.isGridReport && key === 'allValues') {
            continue;
          }
          if (input.indexOf(key) > -1) {
            arr.push(assign(VALUE_TYPES[key], {key: key}));
          }
        }
        return arr;
      };

      if (props.haveName) {
        if (props.isHiddenValueOption) {
          return getValueTypes(['none', 'rangeValue', 'rangePercent', 'allValues']);
        }
        return getValueTypes(['none', 'value', 'rangeValue', 'rangePercent', 'allValues']);
      }

      switch (props.dataType) {
        case DATA_TYPE.TEXT:
        case DATA_TYPE.BOOLEAN:
          return getValueTypes(['value', 'allValues']);
        case DATA_TYPE.DATETIME:
          var dateTimeFormatData = getDateTimeFormatData(props.dateTimeFormatData);
          return getValueTypes(
            getOr(['value', 'rangeValue', 'allValues'], 'settingByGroupBy.SETTINGS_VALUE_TYPE', dateTimeFormatData)
          );
        case DATA_TYPE.TIME:
          return getValueTypes(['value', 'rangeValue', 'allValues']);
        case DATA_TYPE.MONEY:
        case DATA_TYPE.NUMERIC:
          return getValueTypes(['value', 'rangeValue', 'rangePercent', 'allValues']);
        default:
          return getValueTypes(['value', 'rangeValue', 'rangePercent', 'allValues']);
      }
    }

    function changeValueType(newVal: any, newValDetails: any) {
      model.setValueType(newVal.value);
      this.initData();
    }
  }

  renderGrid() {
    const {
      props,
      props: {containerName, fieldStore, haveName, useThresholds, canMove},
      model: {selectedValueType, selectedSettingsList}
    } = this;
    const dataTree = get('dataTree.flattenQuerySourceIncludeCalculatedField', fieldStore);
    const selectedSettingsValue = selectedSettingsList || [];
    const canMoveFinal = !!canMove && selectedValueType !== 'value';
    const canChangeThresholdType = useThresholds && selectedValueType === 'value' && containerName === 'shadingMetric';

    return (
      <table className="table table-bordered u_fullWidth">
        <thead>{renderGridHeader()}</thead>
        <tbody>
        {selectedSettingsValue.map((settingsItem, index) =>
          <SettingsByRangeItem
            {...props}
            fieldContainerName={containerName}
            dataTree={dataTree}
            itemData={settingsItem}
            itemIndex={index}
            valueType={selectedValueType}
            key={settingsItem.id}
            canChangeThresholdType={canChangeThresholdType}
            canMove={canMoveFinal}
            onMove={handleMoveItem}
            onDelete={handleDeleteItem.bind(null, index)}
          />
        )}
        </tbody>
      </table>
    );

    function renderGridHeader() {
      const typeStyle = {
        minWidth: '150px'
      };
      return (
        <tr>
          {canMove ? <th/> : null}
          {canChangeThresholdType && (
            <th style={typeStyle}>
              <label>{localizer.getText('COMMON_LABEL_TYPE')}</label>
            </th>
          )}
          {haveName ? (
            <th>
              <label>{localizer.getText('COMMON_LABEL_NAME')}</label>
            </th>
          ) : null}
          <th className="izenda-SettingByRange_ValueLabel">
            <label>{localizer.getText('COMMON_LABEL_VALUE')}</label>
          </th>
          <th>
            <label>{get('valueProps.title', props)}</label>
          </th>
          {haveName && selectedValueType === 'value' ? null : <th/>}
        </tr>
      );
    }

    function handleDeleteItem(index) {
      selectedSettingsValue.splice(index, 1);
    }

    function handleMoveItem(from, to) {
      if (to < 0) to = 0;
      if (to >= selectedSettingsValue.length) to = selectedSettingsValue.length - 1;
      if (from === to)
        return;
      selectedSettingsValue.splice(to, 0, selectedSettingsValue.splice(from, 1)[0]);
    }
  }

  /* Initialize default data for value settings */
  initData() {
    const haveName = this.props.haveName;
    const model = this.model;
    const props = this.props;
    const selectedValueType = model.selectedValueType;
    const selectedSettingsValue = model.settingsValue[selectedValueType] || [];
    if (
      props.allowNullValueOverwrite && 
      (!haveName && selectedValueType === 'value')
    ) {
      if (!model.settingsValue.value.find(val => val.key === UIConstants.NULL_VALUE)) {
        model.addNewSetting({
          canBeBlank: true,
          forcedDataType: DATA_TYPE.TEXT,
          key: UIConstants.NULL_VALUE,
          text: '',
          id: uniqueId('setting_'),
          functionId: null,
          canDelete: true,
          fields: [new SettingsByRangeItemFieldModel()],
          thresholdType: 'static'
        });
      }
      if (!model.settingsValue.value.find(val => val.key === UIConstants.BLANK_VALUE)) {
        model.addNewSetting({
          canBeBlank: true,
          forcedDataType: DATA_TYPE.TEXT,
          key: UIConstants.BLANK_VALUE,
          text: '',
          id: uniqueId('setting_'),
          functionId: null,
          canDelete: true,
          fields: [new SettingsByRangeItemFieldModel()],
          thresholdType: 'static'
        });
      }
    }

    if (haveName && selectedValueType === 'value' && selectedSettingsValue.length === 0) {
      model.addNewSetting({
        name: localizer.getText('COMMON_LABEL_LOW'),
        key: '',
        text: props.minColor || '',
        id: uniqueId('setting_'),
        functionId: null,
        canDelete: false,
        thresholdType: props.useThresholds ? Constants.THRESHOLD.THRESHOLD_TYPES.STATIC.value : null,
        fields: [new SettingsByRangeItemFieldModel()]
      });
      model.addNewSetting({
        name: localizer.getText('COMMON_LABEL_HIGH'),
        key: '',
        text: props.maxColor || '',
        id: uniqueId('setting_'),
        functionId: null,
        canDelete: false,
        thresholdType: props.useThresholds ? Constants.THRESHOLD.THRESHOLD_TYPES.STATIC.value : null,
        fields: [new SettingsByRangeItemFieldModel()]
      });
    } else {
      forEach(setting => setting.id = uniqueId('setting_'), selectedSettingsValue);
    }
  }

  getThresholdContainer() {
    const customContainers = getOr(null, 'reportPartContent.customContainers', this.props);
    return ReportPartUtils.findCustomContainer(MapConstants.ThresholdContainer, customContainers);
  }

  getThresholdSubtotalsContainer() {
    const customContainers = getOr(null, 'reportPartContent.customContainers', this.props);
    return ReportPartUtils.findCustomContainer(MapConstants.ThresholdSubtotalsContainer, customContainers);
  }

  cleanUpCustomContainers() {
    const customContainers = getOr(null, 'reportPartContent.customContainers', this.props);
    if (!customContainers || !customContainers.length)
      return;
    const {
      configReportPart
    } = this.props;
    // Remove all fields
    ReportPartUtils.removeFieldsFromContainer(
      null, // null means that we need to remove all fields.
      MapConstants.ThresholdContainer,
      customContainers,
      configReportPart);
    ReportPartUtils.removeFieldsFromContainer(
      null, // null means that we need to remove all fields.
      MapConstants.ThresholdSubtotalsContainer,
      customContainers,
      configReportPart);
  }

  /**
   * Update custom container fields.
   * @returns {Promise<*>}
   */
  updateCustomContainers() {
    const {model, props} = this;
    const {selectedValueType} = model;

    // if non-value: do nothing
    if (selectedValueType !== 'value')
      return Promise.resolve();

    const {
      configField,
      fieldStore,
      configReportPart
    } = props;
    const dataTree = get('dataTree.flattenQuerySourceIncludeCalculatedField', fieldStore);

    const updateSubTotalElementFunction = (elem: any, functionData: any) => {
      const subTotal = get('properties.dataFormattings.subTotal', elem);
      subTotal.label = `${functionData.name}(${elem.name})`;
      subTotal.expression = `${functionData.name}([${elem.name}])`;
      subTotal.dataType = functionData.dataType;
      subTotal.function = functionData.name;
    };

    // Remove all fields from containers and start adding new ones.
    this.cleanUpCustomContainers();

    return new Promise(resolve => {
      const allPromises = [];
      const selectedSettings = model.settingsValue[selectedValueType];
      forEach(settingByRangeItem => {
        const {thresholdType, fields, functionId, functions, functionData} = settingByRangeItem;
        const field = fields[0];
        if (!field)
          return;

        if (Constants.THRESHOLD.THRESHOLD_TYPES.DYNAMIC_SUBTOTAL.value === thresholdType) {
          // DYNAMIC_SUBTOTAL
          const dataTreeField = find(
            f => f.id === field.fieldId && f.querySourceDetail.id === field.querySourceId,
            dataTree);
          // Insert field into the container
          const container = this.getThresholdSubtotalsContainer();
          const promise = configReportPart
            .insertFieldToContainer(container, dataTreeField.field, dataTreeField.querySourceDetail, null,
              configField.functionInfo, null, false)
            .then(newElement => {
              // set alias for currently added field element
              const thresholdFieldNameAlias = String.newGuid();
              newElement.name = thresholdFieldNameAlias;
              newElement.field.fieldNameAlias = thresholdFieldNameAlias;
              field.updateFieldName(thresholdFieldNameAlias);

              // Set up function
              const defaultFunctionData = find({name: ReportPartFieldConstants.FUNCTION.COUNT.NAME}, functions);
              const defaultFunctionId = getOr(null, 'id', defaultFunctionData);
              const actualFunctionData = find({id: functionId || defaultFunctionId}, functions);
              updateSubTotalElementFunction(newElement, actualFunctionData);
            });
          allPromises.push(promise);

        } else if (Constants.THRESHOLD.THRESHOLD_TYPES.DYNAMIC.value === thresholdType) {
          // DYNAMIC
          const dataTreeField = find(
            f => f.id === field.fieldId && f.querySourceDetail.id === field.querySourceId,
            dataTree);
          // set alias for currently added field element
          const container = this.getThresholdContainer();
          const promise = configReportPart
            .insertFieldToContainer(container, dataTreeField.field, dataTreeField.querySourceDetail, null,
              functionData, null, false)
            .then(newElement => {
              const thresholdFieldNameAlias = String.newGuid();
              newElement.name = thresholdFieldNameAlias;
              newElement.field.fieldNameAlias = thresholdFieldNameAlias;
              field.updateFieldName(thresholdFieldNameAlias);
            });
          allPromises.push(promise);

        }
      }, selectedSettings);

      if (allPromises.length)
        Promise.all(allPromises).then(resolve);
      else
        resolve();
    });
  }
}



// WEBPACK FOOTER //
// ./ui/modules/ReportDesigner/Fields/PropertiesBuilder/CommonSettings/Izenda.SettingsByRange.js